import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ShareToWallModal from './ShareToWallModal';
import '../App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JamSessionList = ({ isOpen, onClose, onJoinSession, userToken, onShareToDiscord }) => {
  const [publicSessions, setPublicSessions] = useState([]);
  const [mySessions, setMySessions] = useState([]);
  const [activeTab, setActiveTab] = useState('public');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [wallShareSession, setWallShareSession] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchSessions();
    }
  }, [isOpen, activeTab]);

  const fetchSessions = async () => {
    setLoading(true);
    setError('');

    try {
      if (activeTab === 'public') {
        const response = await axios.get(`${API}/jam/public`);
        setPublicSessions(response.data);
      } else {
        const response = await axios.get(`${API}/jam/active`, {
          headers: { 'Authorization': `Bearer ${userToken}` }
        });
        setMySessions(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API}/jam/${sessionId}/join`,
        {},
        {
          headers: { 'Authorization': `Bearer ${userToken}` }
        }
      );
      onJoinSession(response.data);
      onClose();
    } catch (err) {
      console.error('Failed to join session:', err);
      setError(err.response?.data?.detail || 'Failed to join session');
    }
  };

  if (!isOpen) return null;

  const sessions = activeTab === 'public' ? publicSessions : mySessions;

  return (
    <div className="jam-modal-overlay" onClick={onClose}>
      <div className="jam-modal-content jam-list-modal" onClick={(e) => e.stopPropagation()}>
        <div className="jam-modal-header">
          <h2>🎧 Join a Jam Session</h2>
          <button className="jam-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="jam-tabs">
          <button
            className={`jam-tab ${activeTab === 'public' ? 'active' : ''}`}
            onClick={() => setActiveTab('public')}
          >
            🌍 Public Sessions
          </button>
          <button
            className={`jam-tab ${activeTab === 'my' ? 'active' : ''}`}
            onClick={() => setActiveTab('my')}
          >
            🎵 My Sessions
          </button>
        </div>

        <div className="jam-modal-body">
          {loading ? (
            <div className="jam-loading">Loading sessions...</div>
          ) : error ? (
            <div className="jam-error-message">{error}</div>
          ) : sessions.length === 0 ? (
            <div className="jam-empty-state">
              <p>🎵</p>
              <p>No active sessions found</p>
              <p className="jam-help-text">
                {activeTab === 'public' 
                  ? 'Be the first to start a jam!' 
                  : 'Create a session to start listening with friends'}
              </p>
            </div>
          ) : (
            <div className="jam-session-list">
              {sessions.map((session) => (
                <div key={session.session_id} className="jam-session-card">
                  <div className="jam-session-info">
                    <h3>{session.name}</h3>
                    <p className="jam-session-host">
                      🎧 Hosted by {session.host_username}
                    </p>
                    <div className="jam-session-meta">
                      <span className="jam-participant-count">
                        👥 {session.participants.length} listening
                      </span>
                      {session.current_track && (
                        <span className="jam-now-playing">
                          🎵 {session.current_track.title}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setWallShareSession(session);
                      }}
                      title="Share to The Wall"
                      data-testid={`jam-wall-share-${session.session_id}`}
                      style={{
                        padding: '8px 12px',
                        background: 'rgba(6, 182, 212, 0.15)',
                        border: '1px solid #22d3ee',
                        borderRadius: '6px',
                        color: '#22d3ee',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        fontSize: '13px'
                      }}
                    >
                      📣 Wall
                    </button>
                    {onShareToDiscord && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onShareToDiscord({
                            name: session.name,
                            currentTrack: session.current_track?.title || 'Getting started...',
                            listeners: session.participants.length,
                            host: session.host_username,
                            joinUrl: `${window.location.origin}?jam=${session.session_id}`
                          });
                        }}
                        title="Invite via Discord"
                        style={{
                          padding: '8px 12px',
                          background: 'rgba(88, 101, 242, 0.2)',
                          border: '1px solid #5865F2',
                          borderRadius: '6px',
                          color: '#5865F2',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          fontSize: '13px'
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 71 55" fill="#5865F2">
                          <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1099 30.1693C30.1099 34.1136 27.2801 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7637 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.7018 30.1693C53.7018 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                        </svg>
                        Invite
                      </button>
                    )}
                    <button
                      className="jam-btn jam-btn-primary jam-btn-small"
                      onClick={() => handleJoin(session.session_id)}
                    >
                      Join
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="jam-modal-footer">
          <button className="jam-btn jam-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {wallShareSession && (
        <ShareToWallModal
          isOpen={!!wallShareSession}
          onClose={() => setWallShareSession(null)}
          source="jam"
          eventItem={{
            title: wallShareSession.name,
            subtitle: `🎧 Hosted by ${wallShareSession.host_username}`,
            date_line: wallShareSession.current_track ? `🎵 ${wallShareSession.current_track.title}` : null,
            extra: `👥 ${wallShareSession.participants?.length || 0} listening`,
            session_id: wallShareSession.session_id,
            link: `${window.location.origin}?jam=${wallShareSession.session_id}`,
          }}
          currentUser={(() => { try { return JSON.parse(localStorage.getItem('beatseek_user') || 'null'); } catch { return null; } })()}
        />
      )}
    </div>
  );
};

export default JamSessionList;
